/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { isNone } from '@ember/utils';
import LogModel from 'cio/models/log';
import DS from 'ember-data';
import CampaignModel from 'cio/models/campaign';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';

interface MergeEventViewerArgs {
  editableEntity: ComposerEditableEntity;
  campaign?: CampaignModel;
  event: LogModel;
  events: DS.PromiseObject<LogModel[]> & LogModel[];
  isLiquidInsertionDisabled: boolean;
  onInsertLiquid: (event: Event) => unknown;
  onSelectEvent: (event: LogModel) => unknown;
  hasReadOnlyPermission: boolean;
}

export default class MergeEventViewer extends Component<MergeEventViewerArgs> {
  @service facebookLeadAds!: FacebookLeadAdsService;

  get typeName() {
    return this.args.campaign?.isWebhookTriggered ? 'trigger' : 'event';
  }

  @computed('args.event', 'areEventsFulfilled')
  get canViewPreviousEvent() {
    let { event, events } = this.args;
    return events && this.areEventsFulfilled && events.indexOf(event) > 0;
  }

  @computed('args.event', 'areEventsFulfilled')
  get canViewNextEvent() {
    let { event, events } = this.args;
    return events && this.areEventsFulfilled && events.indexOf(event) < events.length - 1;
  }

  @computed('args.campaign.isFormTriggered', 'facebookLeadAds.forms.[]', 'args.event.attrs.form_id')
  get campaignFormName() {
    if (!this.args.campaign?.isFormTriggered) {
      return null;
    }

    let { attrs } = this.args.event;

    if (Array.isArray(attrs)) {
      return '';
    }

    // string coercion for test events: in which form_id can be a number (not string)
    let form = this.facebookLeadAds.formsByFormID[String(attrs.form_id)];

    return form?.formNameForDisplay || '';
  }

  @computed('args.events.[]')
  get areEventsFulfilled() {
    let { events } = this.args;
    if (typeof events.get('isFulfilled') === 'undefined') {
      return true;
    } else {
      return events.get('isFulfilled');
    }
  }

  @computed('args.event', 'args.events')
  get currentEventNumber() {
    let { event, events } = this.args;
    return events.indexOf(event) + 1;
  }

  @computed('args.event')
  get eventAttrs() {
    let attributes = this.args.event?.attrs;

    if (isNone(attributes) || Array.isArray(attributes)) {
      return [];
    }

    return Object.keys(attributes).map((key) => {
      return {
        label: key,
        value: (attributes as Record<string, unknown>)[key],
      };
    });
  }

  @action
  selectNextEvent() {
    this.moveEventBy(1);
  }

  @action
  selectPreviousEvent() {
    this.moveEventBy(-1);
  }

  private moveEventBy(factor: number) {
    let { event, events } = this.args;
    let index = events.indexOf(event);
    let newEvent = events[index + factor] as LogModel;

    this.args.onSelectEvent(newEvent);
  }
}
